import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  ListApplicationsFilterType,
  RecentDuplicateApplicationSelectionType,
} from "@/domain/applications";

import { ApplicationsApi } from "../api/applicationsApi";

export const ApplicationsQueryKeys = {
  all: ["applications"] as const,
  list: (filters?: ListApplicationsFilterType) =>
    filters
      ? ([...ApplicationsQueryKeys.all, "list", { filters }] as const)
      : ([...ApplicationsQueryKeys.all, "list"] as const),
  details: (applicationId: string) =>
    [...ApplicationsQueryKeys.all, applicationId] as const,
  count: (filters?: ListApplicationsFilterType) =>
    [...ApplicationsQueryKeys.all, "count", { filters }] as const,
  recentDuplicates: (
    selectedApplications: RecentDuplicateApplicationSelectionType[],
  ) =>
    [
      ...ApplicationsQueryKeys.all,
      "recent-duplicates",
      { selectedApplications },
    ] as const,
};

type ListApplicationsParamsType = {
  filters: ListApplicationsFilterType;
  isEnabled?: boolean;
};

export const useListApplicationsQuery = ({
  filters,
  isEnabled,
}: ListApplicationsParamsType) => {
  return useQuery({
    queryKey: ApplicationsQueryKeys.list(filters),
    queryFn: () => ApplicationsApi.listApplications(filters),
    enabled: isEnabled,
  });
};

export const useApplicationsCountQuery = (
  filters: ListApplicationsFilterType,
) => {
  return useQuery({
    queryKey: ApplicationsQueryKeys.count(filters),
    queryFn: () => ApplicationsApi.getApplicationsCount(filters),
  });
};

export const useRecentDuplicateApplicationsQuery = () => {
  const queryClient = useQueryClient();

  return {
    fetchRecentDuplicateApplications: (
      selectedApplications: RecentDuplicateApplicationSelectionType[],
    ) =>
      queryClient.fetchQuery({
        queryKey: ApplicationsQueryKeys.recentDuplicates(selectedApplications),
        queryFn: () =>
          ApplicationsApi.listRecentDuplicateApplications(selectedApplications),
        staleTime: 0,
      }),
  };
};

export const useGetApplicationQuery = (applicationId: string) => {
  return useQuery({
    queryKey: ApplicationsQueryKeys.details(applicationId),
    queryFn: () => ApplicationsApi.getApplication(applicationId),
    enabled: Boolean(applicationId),
  });
};
